import { request } from '@/utils/ajax';
import { ConfiguratorData, ConfiguratorSignatureDto, SetSelectionRequest } from './configurator.types';

const API_BASE = '/api/configurator';

export async function getConfiguratorData(residentialUnitId: number, deadlineId?: number): Promise<ConfiguratorData> {
    const url = deadlineId ? `${API_BASE}/${residentialUnitId}/deadline/${deadlineId}` : `${API_BASE}/${residentialUnitId}`;

    return request(url).then((res: Response) => {
        if (!res.ok) throw new Error('Failed to fetch configurator data');
        return res.json();
    });
}

export async function setSelection(
    residentialUnitId: number,
    deadlineId: number,
    selection: SetSelectionRequest
): Promise<ConfiguratorData> {
    return request(`${API_BASE}/${residentialUnitId}/deadline/${deadlineId}`, {
        method: 'POST',
        json: true,
        body: selection,
    }).then((res: Response) => {
        if (!res.ok) throw new Error('Failed to set selection');
        return res.json();
    });
}

/**
 * Recalculate configurator data as-if the given selection were applied, WITHOUT persisting it.
 * Used to show a live price while the buyer edits a manual quantity before saving.
 */
export async function previewSelection(
    residentialUnitId: number,
    deadlineId: number,
    selection: SetSelectionRequest
): Promise<ConfiguratorData> {
    return request(`${API_BASE}/${residentialUnitId}/deadline/${deadlineId}/preview`, {
        method: 'POST',
        json: true,
        body: selection,
    }).then((res: Response) => {
        if (!res.ok) throw new Error('Failed to preview selection');
        return res.json();
    });
}

export async function pickStyle(
    residentialUnitId: number,
    deadlineId: number,
    styleId: number,
    roomId?: number
): Promise<ConfiguratorData> {
    // The room-scoped backend route has no deadline segment (see ConfiguratorController.PickRoomStyle).
    const url = roomId
        ? `${API_BASE}/${residentialUnitId}/style/${styleId}/room/${roomId}`
        : `${API_BASE}/${residentialUnitId}/deadline/${deadlineId}/style/${styleId}`;
    return request(url, { method: 'POST' }).then((res: Response) => {
        if (!res.ok) throw new Error('Failed to pick style');
        return res.json();
    });
}

export async function unpickStyle(
    residentialUnitId: number,
    styleGroupId: number,
    roomId?: number
): Promise<ConfiguratorData> {
    // The backend unpick routes have no deadline segment (see ConfiguratorController.UnpickStyle).
    return request(`${API_BASE}/${residentialUnitId}/style-group/${styleGroupId}${roomId ? `/room/${roomId}` : ''}`, {
        method: 'DELETE',
    }).then((res: Response) => {
        if (!res.ok) throw new Error('Failed to unpick style');
        return res.json();
    });
}

/**
 * Configurator state as-if the style were picked, computed in-memory by the backend.
 * Read-only: nothing is persisted. Use for style detail views so prices reflect what
 * the buyer would actually get after picking the style (e.g. free defaults).
 */
export async function previewStyle(
    residentialUnitId: number,
    styleId: number,
    roomId?: number | null
): Promise<ConfiguratorData> {
    const url = roomId
        ? `${API_BASE}/${residentialUnitId}/style/${styleId}/room/${roomId}/preview`
        : `${API_BASE}/${residentialUnitId}/style/${styleId}/preview`;
    return request(url).then((res: Response) => {
        if (!res.ok) throw new Error('Failed to fetch style preview');
        return res.json();
    });
}

export async function pickDefaults(residentialUnitId: number, deadlineId: number): Promise<ConfiguratorData> {
    return request(`${API_BASE}/${residentialUnitId}/deadline/${deadlineId}/pick-defaults`, {
        method: 'POST',
    }).then((res: Response) => {
        if (!res.ok) throw new Error('Failed to pick defaults');
        return res.json();
    });
}

export async function prepareConfiguratorSignature(
    residentialUnitId: number,
    deadlineId: number,
    profileId: number
): Promise<ConfiguratorSignatureDto> {
    return request(`${API_BASE}/${residentialUnitId}/deadline/${deadlineId}/signature/prepare`, {
        body: { profileId },
        json: true,
        method: 'POST',
    }).then((x) => x.json());
}

export async function getConfiguratorSignature(clientKey: string): Promise<ConfiguratorSignatureDto> {
    return request(`${API_BASE}/signature/${clientKey}`, { method: 'GET' }).then((x) => x.json());
}

export async function signConfigurator(clientKey: string, oneTimeCode: string, profileId: number): Promise<any> {
    return request(`${API_BASE}/signature/${clientKey}`, {
        method: 'POST',
        json: true,
        body: { oneTimeCode, profileId },
    });
}
